/**
 * CreateAdvanceConfirm — the proof-read, one moment before the advance exists.
 *
 * `ActivationConfirm`'S SURFACE AT ADVANCE SCALE, and deliberately its shape down to the class
 * names: every particular the loan v7 Schedule states, in the Schedule's own order and under the
 * Schedule's own row labels, as it will print. NOT "are you sure?" — the last chance anybody has to
 * notice that the weekly repayment is a decimal point out, or that the disbursement picked is the
 * wrong GHS 1,200 line, is the moment before a `loans` row exists and the figure is in
 * `total_value`.
 *
 * THE WORDS COME OUT OF A PURE FUNCTION (`advanceSummary`), so what this dialog asserts is testable
 * without a browser and cannot drift from what the writer is about to send.
 *
 * MOUNTED ONCE, BY THE PAGE. It is a Radix `AlertDialog` portalled to `document.body`, exactly as
 * `ActivationConfirm` is — which is also why it is not rendered from inside the commit bar: the bar
 * is drawn on the repayment canvas and a dialog mounted from a control that can unmount underneath
 * it is a dialog that closes itself mid-confirmation.
 */

import { Loader2 } from 'lucide-react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

import { BRAND_BUTTON } from '../../manifest/NarratorLine';
import type { AdvanceDraft } from '../advanceCommit';
import type { AdvanceSnapshot } from '../advanceState';
import { advanceSummary } from '../advanceSummary';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    draft: AdvanceDraft;
    snapshot: AdvanceSnapshot;
    pending: boolean;
    /** Refused by `validateDraft` — the bar that opens this is disabled for the same reason. */
    blocked: boolean;
    onConfirm: () => void;
}

export default function CreateAdvanceConfirm({
    open,
    onOpenChange,
    draft,
    snapshot,
    pending,
    blocked,
    onConfirm,
}: Props) {
    const summary = advanceSummary(draft, snapshot);

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="sd-tokens max-h-[85vh] max-w-2xl overflow-y-auto">
                <AlertDialogHeader>
                    <AlertDialogTitle>Create this advance?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This records the money as lent and starts the weekly repayment from the day
                        below. Read it back — this is what the Schedule prints.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="sd-clauses">
                    {summary.clauses.map((clause) => (
                        <div className="sd-clause" key={clause.no}>
                            <span className="sd-cl-no">{clause.no}</span>
                            <p className="sd-cl-text">
                                {clause.parts.map((piece, index) => {
                                    if (piece.kind === 'text') return <span key={index}>{piece.text}</span>;
                                    return (
                                        <span
                                            key={index}
                                            className={cn(
                                                'sd-cl-val',
                                                piece.kind === 'missing' && 'sd-cl-val-missing',
                                            )}
                                        >
                                            {piece.text}
                                        </span>
                                    );
                                })}
                            </p>
                        </div>
                    ))}
                </div>

                {/* THE PARTIES ARE ON THE PROOF-READ AND NOT EDITABLE FROM IT. They are
                    render-blocking merge keys fixed on the contract's own areas, and the block on
                    the agreement area links out to each — what belongs here is only the reading. */}
                <div>
                    <p className="sd-hint">{summary.heading}</p>
                    <div className="sd-rows">
                        {summary.facts.map((fact) => (
                            <div className="sd-rec" key={fact.field}>
                                <div className="sd-rec-k">{fact.label}</div>
                                <div className={cn('sd-rec-v sd-num', !fact.value && 'sd-empty')}>
                                    {fact.value ?? '—'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {summary.missing.length > 0 && (
                    <p className="sd-hint text-[hsl(var(--destructive))]">
                        {summary.missing.length === 1
                            ? '1 field this Schedule requires is empty: '
                            : `${summary.missing.length} fields this Schedule requires are empty: `}
                        {summary.missing.map((fact) => fact.label.toLowerCase()).join(', ')}.
                    </p>
                )}

                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        className={BRAND_BUTTON}
                        onClick={(event) => {
                            // The dialog would close itself on click; the write decides when it
                            // closes, so a refusal does not leave the admin staring at a dismissed
                            // surface with no explanation.
                            event.preventDefault();
                            onConfirm();
                        }}
                        disabled={pending || blocked}
                    >
                        {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Create advance
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
